/**
 * Der Draht zur Kammer.
 *
 * Ein dünner Fetch-Client: Token aus dem LocalStorage, Fehler in der Sprache
 * der Kammer, keine Bibliothek. Bei 401 wird die Sitzung verworfen und die
 * Pforte gezeigt — ein abgelaufenes Token ist kein Fehlerzustand, sondern der
 * normale Lauf der Dinge nach 90 Tagen.
 */

const TOKEN_KEY = 'strschgb.token'
const MITGLIED_KEY = 'strschgb.mitglied'

export interface GespeichertesMitglied {
  id: number
  name: string
  avatarEmoji: string
  host?: boolean
}

export function gespeichertesToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export function gespeichertesMitglied(): GespeichertesMitglied | null {
  const roh = localStorage.getItem(MITGLIED_KEY)
  if (!roh) return null
  try {
    return JSON.parse(roh) as GespeichertesMitglied
  } catch {
    return null
  }
}

export function speichereSitzung(token: string, mitglied: GespeichertesMitglied): void {
  localStorage.setItem(TOKEN_KEY, token)
  localStorage.setItem(MITGLIED_KEY, JSON.stringify(mitglied))
}

export function verwerfeSitzung(): void {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(MITGLIED_KEY)
}

/** Fehler mit der Meldung der Kammer, direkt anzeigbar. */
export class ApiFehler extends Error {
  readonly status: number
  readonly code: string

  constructor(status: number, code: string, meldung: string) {
    super(meldung)
    this.name = 'ApiFehler'
    this.status = status
    this.code = code
  }
}

let beiAbmeldung: (() => void) | null = null

/** Wird gerufen, wenn ein Token abgelehnt wurde — die App zeigt die Pforte. */
export function beiSitzungsende(handler: () => void): void {
  beiAbmeldung = handler
}

async function anfrage<T>(pfad: string, init: RequestInit = {}): Promise<T> {
  const token = gespeichertesToken()
  const kopf: Record<string, string> = {}
  // Nur mit Body auch den Typ ansagen — Fastify weist leere JSON-Boedies ab.
  if (init.body !== undefined) kopf['content-type'] = 'application/json'
  if (token) kopf.authorization = `Bearer ${token}`

  let antwort: Response
  try {
    antwort = await fetch(pfad, { ...init, headers: { ...kopf, ...init.headers } })
  } catch {
    throw new ApiFehler(
      0,
      'keine_verbindung',
      'Der Antrag konnte nicht zugestellt werden. Verbindung prüfen und erneut einreichen.',
    )
  }

  if (antwort.status === 401) {
    verwerfeSitzung()
    beiAbmeldung?.()
  }

  if (!antwort.ok) {
    const nutzlast = (await antwort.json().catch(() => null)) as {
      code?: string
      meldung?: string
    } | null
    throw new ApiFehler(
      antwort.status,
      nutzlast?.code ?? 'unbekannt',
      nutzlast?.meldung ?? 'Die Kammer ist vorübergehend nicht arbeitsfähig.',
    )
  }

  return (await antwort.json()) as T
}

export const api = {
  get: <T>(pfad: string): Promise<T> => anfrage<T>(pfad),
  post: <T>(pfad: string, body?: unknown): Promise<T> =>
    anfrage<T>(pfad, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T>(pfad: string, body: unknown): Promise<T> =>
    anfrage<T>(pfad, { method: 'PATCH', body: JSON.stringify(body) }),
  del: <T>(pfad: string, body?: unknown): Promise<T> =>
    anfrage<T>(pfad, {
      method: 'DELETE',
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
}
